import {
  advisorySourceSelectionKey,
  matchesAdvisorySourceName,
  matchesAdvisorySourceUrl,
  sameAdvisoryPassage,
  type AdvisorySourceReferenceV01,
} from './advisoryProvenance';
import type { AdvisoryQuoteReference } from './advisoryQuoteAnchors';
import type { AdvisoryAnswerEvidenceEntry } from './advisoryReportEvidence';

/** A table explicitly cites a source, without asserting an exact quotation match. */
export interface AdvisoryTableSourceReference {
  key: string;
  label: string;
  source: AdvisorySourceReferenceV01;
  sourceValues: readonly string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function tableSourceValues(rendering: unknown): string[] {
  if (!isRecord(rendering) || !Array.isArray(rendering.sections)) return [];
  return rendering.sections.flatMap(section => {
    if (
      !isRecord(section) ||
      section.componentId !== 'evidence-table' ||
      !isRecord(section.data) ||
      !Array.isArray(section.data.rows)
    )
      return [];
    return section.data.rows.flatMap(row =>
      isRecord(row) && typeof row.source === 'string' && row.source.trim()
        ? [row.source.trim()]
        : []
    );
  });
}

/** Prefer explicit identity. Titles may name several passages of one known document. */
export function matchAdvisoryTableSources(
  value: string,
  sources: readonly AdvisorySourceReferenceV01[]
): readonly AdvisorySourceReferenceV01[] {
  const identityMatches = sources.filter(
    source =>
      [source.id, source.canonicalId, source.compositeId, source.documentId].includes(value) ||
      matchesAdvisorySourceUrl(source, value)
  );
  if (identityMatches.length) return identityMatches;
  const titleMatches = sources.filter(source => matchesAdvisorySourceName(source, value));
  if (titleMatches.length <= 1) return titleMatches;
  const documentId = titleMatches[0].documentId;
  return documentId && titleMatches.every(source => source.documentId === documentId)
    ? titleMatches
    : [];
}

/** Allocate after quote labels so adding table links never renumbers existing quotations. */
export function buildAdvisoryTableSourceReferences(
  answers: readonly AdvisoryAnswerEvidenceEntry[],
  quotes: Readonly<Record<string, readonly AdvisoryQuoteReference[]>>
): Record<string, AdvisoryTableSourceReference[]> {
  let number = new Set(
    Object.values(quotes)
      .flat()
      .map(reference => reference.label)
  ).size;
  const labelled: AdvisoryTableSourceReference[] = [];
  return Object.fromEntries(
    answers.map(({ input, evidence }) => {
      const answerKey = input.answerKey || input.questionId;
      const references: AdvisoryTableSourceReference[] = [];
      for (const value of tableSourceValues(input.rendering)) {
        const sources = evidence?.ok
          ? matchAdvisoryTableSources(value, evidence.value.sources)
          : [];
        for (const source of sources) {
          const existing = references.find(reference => reference.source.key === source.key);
          if (existing) {
            if (!existing.sourceValues.includes(value))
              existing.sourceValues = [...existing.sourceValues, value];
            continue;
          }
          const shared = labelled.find(reference => sameAdvisoryPassage(reference.source, source));
          const reference = {
            key: JSON.stringify([answerKey, 'table-source', advisorySourceSelectionKey(source)]),
            label: shared?.label ?? `S${++number}`,
            source,
            sourceValues: [value],
          };
          references.push(reference);
          labelled.push(reference);
        }
      }
      return [answerKey, references];
    })
  );
}
